export const FEATURE_SHOWCASE_CONTENT = {
    "booking-scheduling": {
        heading: "Flawless Booking Flow",
        intro: "Keep your calendar full with real-time availability, smart reminders, and seamless rebooking.",
        tabs: [
            {
                value: "live-availability",
                label: "Live Availability",
                title: "Show true availability instantly",
                description: "Sync services, team member hours, and buffers to prevent double-booking and guesswork.",
                items: [
                    "Syncs service duration and gaps",
                    "Reflects team-member-specific hours",
                    "Eliminates double-booking risks"
                ],
                badge: "Accurate Calendar"
            },
            {
                value: "smart-reminders",
                label: "Smart Reminders",
                title: "Reduce no-shows with automated nudges",
                description: "Send timely, personalized SMS and email reminders to ensure clients arrive prepared.",
                items: [
                    "Automated SMS & email schedules",
                    "Customized appointment details",
                    "Dramatically reduces missed slots"
                ],
                badge: "Fewer Empty Chairs"
            },
            {
                value: "team-visibility",
                label: "Team Visibility",
                title: "Clear schedules for every provider",
                description: "Empower your team members with real-time insights into daily schedules, clients, and pacing.",
                items: [
                    "Organized daily provider schedules",
                    "Upfront client booking details",
                    "Early conflict resolution"
                ],
                badge: "Perfect Alignment"
            },
            {
                value: "rebooking-flow",
                label: "Rebooking Flow",
                title: "Turn visits into habits",
                description: "Seamlessly prompt rebooking at checkout and share quick booking links.",
                items: [
                    "Effortless next-visit confirmations",
                    "Instant shareable booking links",
                    "Consistent client retention"
                ],
                badge: "Built-In Retention"
            }
        ]
    },
    "staff-management": {
        heading: "Effortless Team Management",
        intro: "Run schedules, permissions, and commissions smoothly without the admin headache.",
        tabs: [
            {
                value: "roles-access",
                label: "Roles & Access",
                title: "Precise control over business access",
                description: "Assign specific roles to ensure team members have the exact tools they need, keeping financials secure.",
                items: [
                    "Custom front desk & team member roles",
                    "Secured financial controls",
                    "Targeted tool access"
                ],
                badge: "Secure Operations"
            },
            {
                value: "shift-planning",
                label: "Shift Planning",
                title: "Smart scheduling for real demand",
                description: "Align team member hours with peak periods to balance coverage and prevent burnout.",
                items: [
                    "Flexible provider availability",
                    "Clear time-off visibility",
                    "Proactive gap coverage"
                ],
                badge: "Optimized Coverage"
            },
            {
                value: "commissions",
                label: "Commissions",
                title: "Transparent, automated earnings",
                description: "Automatically link services and tips to providers for fast, error-free commission payouts.",
                items: [
                    "Direct sales attribution",
                    "Connected tip & service metrics",
                    "Automated payout math"
                ],
                badge: "Zero Payroll Stress"
            },
            {
                value: "performance",
                label: "Performance",
                title: "Actionable team member insights",
                description: "Track revenue and retention by provider to reward top performers and guide coaching.",
                items: [
                    "Quick service & retail reviews",
                    "Team trend comparisons",
                    "Data-driven coaching"
                ],
                badge: "Measurable Growth"
            }
        ]
    },
    sales: {
        heading: "Actionable Revenue Insights",
        intro: "Turn daily sales data into clear, immediate strategies instead of waiting for end-of-month reports.",
        tabs: [
            {
                value: "daily-dashboard",
                label: "Daily Dashboard",
                title: "Real-time revenue tracking",
                description: "Monitor bookings, retail, and tips as they happen to stay ahead of your daily goals.",
                items: [
                    "Live revenue snapshots",
                    "Quick activity reviews",
                    "Immediate performance pulse"
                ],
                badge: "Live Visibility"
            },
            {
                value: "service-mix",
                label: "Service Mix",
                title: "Identify your best margins",
                description: "See which services drive the most profit to confidently adjust pricing and promotions.",
                items: [
                    "Highlight top performers",
                    "Spot low-margin services",
                    "Data-backed pricing"
                ],
                badge: "Maximized Margins"
            },
            {
                value: "staff-sales",
                label: "Team Member Sales",
                title: "Measure provider impact",
                description: "Evaluate team members based on upselling, retention, and overall revenue generation.",
                items: [
                    "Individual sales tracking",
                    "Upsell comparisons",
                    "Contextual coaching data"
                ],
                badge: "Clear Team Trends"
            },
            {
                value: "exportable-reporting",
                label: "Exportable Reports",
                title: "Audit-ready financials",
                description: "Generate clean, accurate exports for accounting, taxes, and strategic planning.",
                items: [
                    "Structured data formats",
                    "Effortless tax exports",
                    "Unified financial truth"
                ],
                badge: "Stress-Free Taxes"
            }
        ]
    },
    "multi-location": {
        heading: "Every Location, One Command Center",
        intro: "Run all your locations from a single account without juggling logins or duplicating setup.",
        tabs: [
            {
                value: "unified-account",
                label: "Unified Account",
                title: "One login for the whole business",
                description: "Manage services, pricing, and settings for every location from a single business account.",
                items: [
                    "Single account across locations",
                    "Shared services and pricing setup",
                    "No duplicate administration"
                ],
                badge: "Zero Juggling"
            },
            {
                value: "location-teams",
                label: "Location Teams",
                title: "The right team at the right place",
                description: "Assign team members to their home location so schedules, bookings, and payroll stay accurate.",
                items: [
                    "Per-location team assignments",
                    "Primary location for payroll",
                    "Accurate location schedules"
                ],
                badge: "Organized Teams"
            },
            {
                value: "location-taxes",
                label: "Accurate Taxes",
                title: "Payroll taxes that match the workplace",
                description: "Each employee's payroll is anchored to their actual work location, so state and local taxes are always right.",
                items: [
                    "Location-accurate work addresses",
                    "Correct state & local withholding",
                    "Compliance without spreadsheets"
                ],
                badge: "Tax Compliant"
            },
            {
                value: "cross-location-reporting",
                label: "Cross-Location Reports",
                title: "Compare performance at a glance",
                description: "See revenue, bookings, and team performance side by side to spot your strongest locations.",
                items: [
                    "Side-by-side location revenue",
                    "Per-location booking trends",
                    "Data-driven expansion decisions"
                ],
                badge: "Full Visibility"
            }
        ]
    },
    payroll: {
        heading: "Effortless Payroll Runs",
        intro: "Connect tracked hours, tips, and commissions directly to paychecks — no manual math, no spreadsheets.",
        tabs: [
            {
                value: "one-click-push",
                label: "One-Click Push",
                title: "From pay period to paycheck instantly",
                description: "Close a pay period and push hours, tips, and commissions to Gusto in a single click.",
                items: [
                    "Automatic earnings collection",
                    "One-click payroll submission",
                    "No manual data re-entry"
                ],
                badge: "Minutes, Not Hours"
            },
            {
                value: "time-tracking",
                label: "Time Tracking",
                title: "Hours captured as they happen",
                description: "Clock-ins, breaks, and time off flow straight into payroll so every worked hour is paid correctly.",
                items: [
                    "Built-in clock-in tracking",
                    "Time-off handled automatically",
                    "Accurate hours every period"
                ],
                badge: "Every Hour Counts"
            },
            {
                value: "tips-commissions",
                label: "Tips & Commissions",
                title: "Earnings attributed automatically",
                description: "Service sales, retail commissions, and tips are linked to each provider — payouts are always right.",
                items: [
                    "Automatic tip attribution",
                    "Commission math done for you",
                    "Transparent earnings breakdown"
                ],
                badge: "Zero Disputes"
            },
            {
                value: "taxes-deposits",
                label: "Taxes & Deposits",
                title: "Compliant pay without the paperwork",
                description: "Gusto handles tax withholding, filings, and direct deposit so your team gets paid legally and on time.",
                items: [
                    "Automated tax withholding",
                    "Direct deposit to bank accounts",
                    "Filings handled by Gusto"
                ],
                badge: "Fully Compliant"
            }
        ]
    },
    "client-management": {
        heading: "Deepen Client Relationships",
        intro: "Remember every preference and past visit so every client feels uniquely valued.",
        tabs: [
            {
                value: "client-profiles",
                label: "Client Profiles",
                title: "Complete details at a glance",
                description: "Access preferences, notes, and history instantly to personalize every appointment.",
                items: [
                    "Centralized client notes",
                    "Pre-visit quick reviews",
                    "Shared team context"
                ],
                badge: "Perfect Prep"
            },
            {
                value: "consultation-history",
                label: "Consultation History",
                title: "Seamless service continuity",
                description: "Track formulas and outcomes so clients get consistent results without repeating themselves.",
                items: [
                    "Organized service logs",
                    "Continuity of care notes",
                    "Informed repeat visits"
                ],
                badge: "Consistent Quality"
            },
            {
                value: "waivers-intake",
                label: "Waivers & Intake",
                title: "Frictionless digital forms",
                description: "Collect necessary paperwork ahead of time to speed up check-in and stay organized.",
                items: [
                    "Easy digital tracking",
                    "Lightning-fast check-ins",
                    "Centralized secure data"
                ],
                badge: "Paperless Flow"
            },
            {
                value: "retention-notes",
                label: "Retention Notes",
                title: "Smarter client follow-ups",
                description: "Identify inactive clients and buying patterns to trigger perfectly timed outreach.",
                items: [
                    "Spot returning behavior",
                    "Targeted follow-ups",
                    "Deliberate retention"
                ],
                badge: "Lasting Loyalty"
            }
        ]
    },
    "online-booking": {
        heading: "Convert Interest into Bookings 24/7",
        intro: "Provide a polished, frictionless booking experience that turns visitors into confirmed appointments.",
        tabs: [
            {
                value: "booking-page",
                label: "Booking Page",
                title: "A premium booking experience",
                description: "Offer a branded, trustworthy portal for clients to book services anytime.",
                items: [
                    "Polished, branded flow",
                    "Self-service booking",
                    "Accessible key details"
                ],
                badge: "Always Open"
            },
            {
                value: "direct-links",
                label: "Direct Links",
                title: "Skip the extra steps",
                description: "Share deep links that take clients straight to specific services or providers.",
                items: [
                    "Direct campaign paths",
                    "Personal provider links",
                    "Reduced drop-off rates"
                ],
                badge: "Instant Checkout"
            },
            {
                value: "social-booking",
                label: "Social Booking",
                title: "Book directly from social",
                description: "Guide Instagram and TikTok traffic into a fast, mobile-optimized booking flow.",
                items: [
                    "Smooth mobile experience",
                    "Easy link placement",
                    "Rapid discovery-to-booking"
                ],
                badge: "Higher Conversions"
            },
            {
                value: "checkout-confidence",
                label: "Checkout Confidence",
                title: "Clear, trustworthy checkout",
                description: "Display upfront pricing and timing to eliminate hesitation and secure the booking.",
                items: [
                    "Transparent pricing",
                    "Clear expectations",
                    "Dependable journey"
                ],
                badge: "Guaranteed Confirmations"
            }
        ]
    },
    memberships: {
        heading: "Automate Recurring Revenue",
        intro: "Create compelling membership packages that build loyalty and predictable income.",
        tabs: [
            {
                value: "recurring-plans",
                label: "Recurring Plans",
                title: "Steady monthly income",
                description: "Launch memberships with clear perks to drive predictable, recurring revenue.",
                items: [
                    "Easy-to-sell plans",
                    "Reliable cash flow",
                    "Stronger client engagement"
                ],
                badge: "Predictable Revenue"
            },
            {
                value: "package-balances",
                label: "Package Balances",
                title: "Transparent usage tracking",
                description: "Keep remaining visits and credits visible to eliminate confusion for team members and clients.",
                items: [
                    "Instant balance checks",
                    "Faster front desk answers",
                    "Zero usage confusion"
                ],
                badge: "Clear Balances"
            },
            {
                value: "renewal-recovery",
                label: "Renewal Recovery",
                title: "Seamless membership renewals",
                description: "Spot expiring plans and prompt renewals before loyal clients slip away.",
                items: [
                    "Proactive expiration alerts",
                    "Timely renewal prompts",
                    "Retained client value"
                ],
                badge: "Higher Retention"
            },
            {
                value: "vip-retention",
                label: "VIP Retention",
                title: "Reward your top clients",
                description: "Deepen loyalty by offering exclusive perks to your highest-value returning clients.",
                items: [
                    "Recognized repeat clients",
                    "Long-term retention strategies",
                    "Measurable loyalty metrics"
                ],
                badge: "Compound Growth"
            }
        ]
    },
    "inventory-retail-management": {
        heading: "Seamless Retail & Stock Control",
        intro: "Connect checkout with live inventory to maximize sales without running out of stock.",
        tabs: [
            {
                value: "stock-visibility",
                label: "Stock Visibility",
                title: "Always know your stock",
                description: "Live inventory updates ensure team members only recommend products you actually have.",
                items: [
                    "Live stock updates",
                    "Fast availability checks",
                    "Reliable recommendations"
                ],
                badge: "Accurate Inventory"
            },
            {
                value: "retail-checkout",
                label: "Retail Checkout",
                title: "Frictionless add-on sales",
                description: "Add products to service tickets instantly to boost revenue without slowing the line.",
                items: [
                    "Easy retail add-ons",
                    "Faster checkout process",
                    "Natural product upselling"
                ],
                badge: "Increased Sales"
            },
            {
                value: "reorder-timing",
                label: "Reorder Timing",
                title: "Never miss a sale",
                description: "Get alerted on low stock to reorder popular items before shelves go empty.",
                items: [
                    "Early low-stock alerts",
                    "Proactive reordering",
                    "Prevented lost revenue"
                ],
                badge: "No More Stockouts"
            },
            {
                value: "product-insights",
                label: "Product Insights",
                title: "Data-driven retail strategy",
                description: "Identify best-sellers and slow movers to optimize your shelf space and margins.",
                items: [
                    "Highlight top products",
                    "Phase out slow movers",
                    "Smarter retail strategy"
                ],
                badge: "Optimized Shelves"
            }
        ]
    },
    payments: {
        heading: "Fast, Secure, Modern Payments",
        intro: "Accept payments effortlessly anywhere while keeping back-office reconciliation clean.",
        tabs: [
            {
                value: "tap-to-pay",
                label: "Tap to Pay",
                title: "Frictionless in-person payments",
                description: "Accept contactless payments instantly to wrap up appointments with a modern touch.",
                items: [
                    "Zero hardware setup",
                    "Lightning-fast checkout",
                    "Modern client experience"
                ],
                badge: "Instant Payments"
            },
            {
                value: "deposits-cards",
                label: "Deposits & Cards",
                title: "Secure your calendar",
                description: "Collect upfront deposits and securely save cards to protect against no-shows.",
                items: [
                    "Protected high-value slots",
                    "Fast repeat checkouts",
                    "Clear payment rules"
                ],
                badge: "Secured Bookings"
            },
            {
                value: "payout-speed",
                label: "Payout Speed",
                title: "Faster access to funds",
                description: "Reduce delays between sales and payouts to keep your business cash flow healthy.",
                items: [
                    "Faster fund availability",
                    "Easier cash flow planning",
                    "Reduced operational stress"
                ],
                badge: "Healthy Cash Flow"
            },
            {
                value: "safer-checkout",
                label: "Safer Checkout",
                title: "A trustworthy experience",
                description: "Provide a secure, professional payment flow that instills confidence in every client.",
                items: [
                    "Clear payment expectations",
                    "Polished brand experience",
                    "Consistent transactions"
                ],
                badge: "Maximum Trust"
            }
        ]
    },
    invoices: {
        heading: "Effortless Invoicing & Collection",
        intro: "Create, send, and track invoices instantly to ensure you get paid without the chase.",
        tabs: [
            {
                value: "quick-creation",
                label: "Quick Creation",
                title: "Generate invoices in seconds",
                description: "Quickly build professional invoices right after service to keep revenue moving.",
                items: [
                    "Instant invoice prep",
                    "Automated formatting",
                    "Clear client records"
                ],
                badge: "Faster Billing"
            },
            {
                value: "pay-links",
                label: "Pay Links",
                title: "Flexible remote payments",
                description: "Send direct payment links so clients can easily settle balances from their phones.",
                items: [
                    "Zero phone call payments",
                    "Self-service settlements",
                    "Faster balance closures"
                ],
                badge: "Easy Remote Pay"
            },
            {
                value: "status-tracking",
                label: "Status Tracking",
                title: "Complete payment visibility",
                description: "Monitor pending, paid, and overdue invoices instantly to target your follow-ups.",
                items: [
                    "Quick status reviews",
                    "Targeted follow-ups",
                    "Zero payment guesswork"
                ],
                badge: "Total Visibility"
            },
            {
                value: "deposit-recovery",
                label: "Deposit Recovery",
                title: "Secure commitments upfront",
                description: "Invoice for deposits early to confirm appointments and drastically reduce cancellations.",
                items: [
                    "Pre-visit deposit requests",
                    "Frictionless confirmations",
                    "Protected early revenue"
                ],
                badge: "Protected Revenue"
            }
        ]
    },
    branding: {
        heading: "Unmistakable Brand Presence",
        intro: "Maintain a premium, cohesive brand aesthetic across every single digital touchpoint.",
        tabs: [
            {
                value: "branded-touchpoints",
                label: "Branded Touchpoints",
                title: "Consistent end-to-end identity",
                description: "Carry your unique visual style through bookings, confirmations, and reminders.",
                items: [
                    "Cohesive client journey",
                    "Daily brand visibility",
                    "Strengthened client trust"
                ],
                badge: "Stronger Identity"
            },
            {
                value: "booking-polish",
                label: "Booking Polish",
                title: "A premium first impression",
                description: "Deliver an impeccably designed booking page that reflects your high service standards.",
                items: [
                    "Premium booking aesthetics",
                    "Stunning first impressions",
                    "High perceived value"
                ],
                badge: "Elevated Booking"
            },
            {
                value: "gift-card-design",
                label: "Gift Card Design",
                title: "Beautiful digital gifting",
                description: "Sell gorgeous digital gift cards that turn loyal clients into brand ambassadors.",
                items: [
                    "Elevated gift presentation",
                    "Brand-aligned referrals",
                    "Easy seasonal sales"
                ],
                badge: "Premium Gifting"
            },
            {
                value: "design-consistency",
                label: "Design Consistency",
                title: "Scale your aesthetic",
                description: "Maintain visual harmony as you grow, ensuring marketing assets never look improvised.",
                items: [
                    "Effortless visual consistency",
                    "Professional marketing assets",
                    "Undiluted brand experience"
                ],
                badge: "Scalable Brand"
            }
        ]
    },
    marketing: {
        heading: "Smarter, Targeted Marketing",
        intro: "Drive repeat business with automated, data-backed campaigns that actually convert.",
        tabs: [
            {
                value: "campaign-targeting",
                label: "Campaign Targeting",
                title: "Precision promotions",
                description: "Leverage visit history to send highly relevant offers to the right clients.",
                items: [
                    "Strategic audience selection",
                    "Highly relevant campaigns",
                    "Maximum marketing ROI"
                ],
                badge: "Better Relevance"
            },
            {
                value: "fill-open-time",
                label: "Fill Open Time",
                title: "Turn gaps into revenue",
                description: "Instantly promote sudden calendar openings to recover lost revenue opportunities.",
                items: [
                    "Rapid slot promotion",
                    "Immediate downtime reaction",
                    "Recovered calendar gaps"
                ],
                badge: "Zero Downtime"
            },
            {
                value: "win-back",
                label: "Win-Back",
                title: "Recover lapsed clients",
                description: "Deploy automated campaigns to re-engage inactive clients without manual effort.",
                items: [
                    "Identify inactive clients",
                    "Automated follow-ups",
                    "Scalable retention"
                ],
                badge: "Second Chances"
            },
            {
                value: "automated-milestones",
                label: "Automated Milestones",
                title: "Celebrate every moment",
                description: "Automatically send thoughtful birthday and anniversary messages to boost loyalty.",
                items: [
                    "Consistent milestone outreach",
                    "Personalized engagement",
                    "Zero repetitive tasks"
                ],
                badge: "Automated Loyalty"
            }
        ]
    },
    "resource-management": {
        heading: "Every Resource Booked Right",
        intro: "Assign rooms, chairs, and equipment to appointments so nothing gets double-booked.",
        tabs: [
            {
                value: "rooms-spaces",
                label: "Rooms & Spaces",
                title: "Give every appointment a place",
                description: "Attach treatment rooms, chairs, or stations to services so the right space is always reserved.",
                items: [
                    "Assign rooms and stations per service",
                    "Capacity-aware scheduling",
                    "No overlapping space bookings"
                ],
                badge: "Organized Spaces"
            },
            {
                value: "shared-equipment",
                label: "Shared Equipment",
                title: "Never overbook shared tools",
                description: "Track limited equipment and machines so two appointments never claim the same resource.",
                items: [
                    "Reserve shared equipment automatically",
                    "Block conflicts before they happen",
                    "Maximize use of every asset"
                ],
                badge: "Conflict-Free"
            },
            {
                value: "live-availability",
                label: "Live Availability",
                title: "See resources at a glance",
                description: "View real-time availability of rooms and resources right alongside your calendar.",
                items: [
                    "Real-time resource view",
                    "Filter the calendar by resource",
                    "Plan busy days with confidence"
                ],
                badge: "Full Visibility"
            }
        ]
    },
    "intake-forms": {
        heading: "Paperless Client Intake",
        intro: "Collect details, consent, and waivers before clients ever walk in.",
        tabs: [
            {
                value: "custom-forms",
                label: "Custom Forms",
                title: "Build the form you need",
                description: "Create tailored intake forms and waivers with your own questions, no code required.",
                items: [
                    "Drag-and-drop custom fields",
                    "Reusable form templates",
                    "Required questions and consent"
                ],
                badge: "Fully Customizable"
            },
            {
                value: "auto-send",
                label: "Auto Send",
                title: "Get answers before the visit",
                description: "Automatically send forms at booking so clients arrive prepared and check-in is instant.",
                items: [
                    "Auto-send on booking or reminder",
                    "Mobile-friendly signing",
                    "Faster front-desk check-in"
                ],
                badge: "Ready On Arrival"
            },
            {
                value: "client-history",
                label: "Client History",
                title: "Keep every response on file",
                description: "Store signed forms and answers on each client profile for a complete, searchable history.",
                items: [
                    "Responses saved to client profiles",
                    "Signed waivers stored securely",
                    "Full history at your fingertips"
                ],
                badge: "Always On Record"
            }
        ]
    },
    "soap-notes": {
        heading: "Treatment Notes Done Right",
        intro: "Record every visit in the SOAP format, from the first note to the final signature.",
        tabs: [
            {
                value: "templates",
                label: "Templates",
                title: "Start every note in seconds",
                description: "Build SOAP templates for each type of treatment, so your team only fills in what changed.",
                items: [
                    "Subjective, Objective, Assessment and Plan",
                    "Default note and images per template",
                    "One template for every treatment"
                ],
                badge: "Faster Notes"
            },
            {
                value: "on-the-appointment",
                label: "On The Appointment",
                title: "Notes tied to every visit",
                description: "Add a SOAP note right from the appointment or the client's profile, and it stays linked to that visit.",
                items: [
                    "Add from the calendar or client profile",
                    "One note per appointment",
                    "Images you can draw and write on"
                ],
                badge: "Always In Context"
            },
            {
                value: "sign-and-lock",
                label: "Sign & Lock",
                title: "Signed, locked and on record",
                description: "Team members sign with a typed or drawn signature, then lock the note so it can't be changed.",
                items: [
                    "Typed, drawn or both signatures",
                    "Require a signature before locking",
                    "Locked notes can't be edited"
                ],
                badge: "Records You Can Trust"
            }
        ]
    },
    "gift-cards": {
        heading: "Gifts That Bring Clients Back",
        intro: "Sell, send, and redeem digital gift cards without lifting a finger.",
        tabs: [
            {
                value: "sell-online",
                label: "Sell Online",
                title: "Your gift shop never closes",
                description: "Clients buy gift cards right from your booking page, any time of day, and you get paid upfront.",
                items: [
                    "Gift card shop on your booking page",
                    "Sell in-store at checkout too",
                    "Run bonus-value promotions"
                ],
                badge: "Revenue Upfront"
            },
            {
                value: "branded-delivery",
                label: "Branded Delivery",
                title: "A gift worth opening",
                description: "Choose eye-catching card designs and send them by email with a personal note, instantly or on the big day.",
                items: [
                    "Beautiful card artwork",
                    "Personal gift messages",
                    "Schedule delivery for any date"
                ],
                badge: "Made to Delight"
            },
            {
                value: "easy-redemption",
                label: "Easy Redemption",
                title: "Redeem in a tap",
                description: "Apply gift cards at checkout and let Zobliss track every balance and transaction for you.",
                items: [
                    "Apply cards at checkout",
                    "Automatic balance tracking",
                    "Full transaction history"
                ],
                badge: "Always Balanced"
            }
        ]
    },
    "pos-kiosk": {
        heading: "Checkout Without the Front Desk",
        intro: "Turn any tablet into a fast, touch-first checkout for your front desk.",
        tabs: [
            {
                value: "kiosk-mode",
                label: "Kiosk Mode",
                title: "Every action, one tap away",
                description: "Switch the calendar to Kiosk view for large touch buttons, then go full screen so the front desk tablet shows nothing else.",
                items: [
                    "Walk-in, waitlist and bookings always on screen",
                    "Pick a client or ring up a walk-in",
                    "Built for touch, no menus to open"
                ],
                badge: "Front Desk Ready"
            },
            {
                value: "fast-checkout",
                label: "Fast Checkout",
                title: "Ring it all up in one place",
                description: "Sell services, products, and tips from a single touchscreen built for speed.",
                items: [
                    "Services and products in one cart",
                    "One-tap tipping",
                    "Quick, guided transactions"
                ],
                badge: "Speedy Sales"
            },
            {
                value: "in-person-payments",
                label: "In-Person Payments",
                title: "Take payment on the spot",
                description: "Accept in-person payments and send receipts by print or email instantly.",
                items: [
                    "Accept cards and tap-to-pay",
                    "Printed or emailed receipts",
                    "Reconciles with your sales reports"
                ],
                badge: "Paid Instantly"
            }
        ]
    },
    reports: {
        heading: "Clear Business Insights",
        intro: "Know exactly what's working with live dashboards and reports built for salons and spas.",
        tabs: [
            {
                value: "performance-dashboard",
                label: "Performance Dashboard",
                title: "Your business at a glance",
                description: "Track sales, appointments, and client numbers on one dashboard and compare them with any past period.",
                items: [
                    "Key numbers on one screen",
                    "Compare with previous periods",
                    "Filter by location and team member"
                ],
                badge: "Live Overview"
            },
            {
                value: "sales-finance",
                label: "Sales & Finance",
                title: "Every sale, accounted for",
                description: "Open ready-made sales, transaction, and sales tax reports whenever you need them.",
                items: [
                    "Sales and transaction detail reports",
                    "Sales tax report for filing",
                    "Retail and gift card reports"
                ],
                badge: "Tax-Ready"
            },
            {
                value: "filters-exports",
                label: "Filters & Exports",
                title: "Find the exact numbers you need",
                description: "Filter any report by date, location, or team member, then email, print, or download it.",
                items: [
                    "Flexible report filters",
                    "Email reports to your accountant",
                    "Print or download in seconds"
                ],
                badge: "Share Anywhere"
            },
            {
                value: "report-access",
                label: "Report Access",
                title: "The right people see the right data",
                description: "Choose which roles can open reports so sensitive financial numbers stay private.",
                items: [
                    "Role-based report access",
                    "Keep financials private",
                    "Give managers what they need"
                ],
                badge: "Secure Data"
            }
        ]
    },
    "data-transfer": {
        heading: "Switching Made Simple",
        intro: "Bring your clients, services, and products with you. We'll handle the move at no cost.",
        tabs: [
            {
                value: "client-import",
                label: "Client Import",
                title: "Bring every client with you",
                description: "Move your whole client list into Zobliss from a CSV file instead of adding clients one by one.",
                items: [
                    "Upload a CSV of your clients",
                    "Download a ready-made template",
                    "Start booking right away"
                ],
                badge: "No Retyping"
            },
            {
                value: "service-import",
                label: "Service Menu Import",
                title: "Your service menu, ready on day one",
                description: "Import your services with their prices and durations so your booking site is ready to go.",
                items: [
                    "Bulk import services",
                    "Keep prices and durations",
                    "Online booking ready fast"
                ],
                badge: "Fast Setup"
            },
            {
                value: "product-import",
                label: "Product Import",
                title: "Stock your shelves in minutes",
                description: "Import your retail products from a CSV file and start tracking stock straight away.",
                items: [
                    "Bulk product upload",
                    "Stock levels included",
                    "Sell products from day one"
                ],
                badge: "Inventory Ready"
            }
        ]
    },
    notifications: {
        heading: "Messages That Send Themselves",
        intro: "Keep every client informed before and after their visit without lifting a finger.",
        tabs: [
            {
                value: "reminders",
                label: "Reminders",
                title: "Fewer no-shows, automatically",
                description: "Send appointment reminders by email and text so clients always remember their visit.",
                items: [
                    "Email and SMS reminders",
                    "Set when reminders go out",
                    "Edit the message wording"
                ],
                badge: "Fewer No-Shows"
            },
            {
                value: "appointment-updates",
                label: "Appointment Updates",
                title: "Clients always know what changed",
                description: "Clients get notified automatically when an appointment is booked, rescheduled, or cancelled.",
                items: [
                    "Booking confirmations",
                    "Reschedule and cancellation notices",
                    "No manual follow-up calls"
                ],
                badge: "Always Informed"
            },
            {
                value: "waitlist",
                label: "Waitlist",
                title: "Fill openings the moment they appear",
                description: "Let waitlisted clients know automatically when a spot opens up for them.",
                items: [
                    "Automatic waitlist messages",
                    "Turn cancellations into bookings",
                    "Happier, faster clients"
                ],
                badge: "Fuller Calendar"
            },
            {
                value: "increase-bookings",
                label: "Increase Bookings",
                title: "Keep clients coming back",
                description: "Send thank-you notes, rebooking reminders, receipts, and birthday messages on autopilot.",
                items: [
                    "Thank-you and rebooking messages",
                    "Birthday messages",
                    "Payment receipts and gift card emails"
                ],
                badge: "Automated Loyalty"
            }
        ]
    },
    "email-marketing": {
        heading: "Emails Clients Actually Open",
        intro: "Share offers, news, and reminders to book again with email campaigns that take minutes to build.",
        tabs: [
            {
                value: "compose",
                label: "Compose",
                title: "Write a campaign in minutes",
                description: "Add a subject, message, and image to build a professional email without any design skills.",
                items: [
                    "Simple campaign editor",
                    "Add images and offers",
                    "Looks great on any device"
                ],
                badge: "Easy Design"
            },
            {
                value: "audience",
                label: "Choose Clients",
                title: "Send to the right people",
                description: "Pick clients one by one or use filters to reach lapsed clients, top spenders, and more.",
                items: [
                    "Select or filter clients",
                    "Target lapsed clients",
                    "Reward your best spenders"
                ],
                badge: "Better Targeting"
            },
            {
                value: "templates",
                label: "Templates",
                title: "Reuse what works",
                description: "Save your best campaigns as templates so the next email takes seconds.",
                items: [
                    "Reusable marketing templates",
                    "Consistent brand look",
                    "Faster campaign setup"
                ],
                badge: "Save Time"
            },
            {
                value: "review-send",
                label: "Review & Send",
                title: "Check it, then send it",
                description: "Review the message and recipients before sending, then see every sent campaign in one list.",
                items: [
                    "Final review before sending",
                    "Sent campaign history",
                    "Filter by email or text"
                ],
                badge: "Send With Confidence"
            }
        ]
    },
    "text-marketing": {
        heading: "Reach Clients Instantly",
        intro: "Text campaigns are perfect for flash offers, last-minute openings, and time-sensitive news.",
        tabs: [
            {
                value: "text-campaigns",
                label: "Text Campaigns",
                title: "Short messages, fast results",
                description: "Write a text, add an image if you like, and send it to your clients in a few clicks.",
                items: [
                    "Quick SMS campaigns",
                    "Optional image messages",
                    "Great for last-minute offers"
                ],
                badge: "Instant Reach"
            },
            {
                value: "targeting",
                label: "Client Filters",
                title: "Only the clients who care",
                description: "Use filters to text the clients most likely to book, like regulars or clients you haven't seen lately.",
                items: [
                    "Filter by visits and spend",
                    "Win back inactive clients",
                    "Less noise, more bookings"
                ],
                badge: "Smart Targeting"
            },
            {
                value: "credits",
                label: "SMS Credits",
                title: "Simple, clear pricing",
                description: "Text campaigns use SMS credits: 1 credit per text-only message, 3 with an image. Top up anytime.",
                items: [
                    "See credits before you send",
                    "Buy more credits anytime",
                    "No surprise charges"
                ],
                badge: "Pay As You Go"
            },
            {
                value: "history",
                label: "Message History",
                title: "Know every message was delivered",
                description: "Check every email and text sent to clients, including campaigns and automated messages.",
                items: [
                    "Full message history",
                    "Delivery statuses",
                    "Search by client"
                ],
                badge: "Full Visibility"
            }
        ]
    }
};

export function getFeatureShowcaseContent(slug, feature) {
    const showcase = FEATURE_SHOWCASE_CONTENT[slug];

    if (showcase) {
        return showcase;
    }

    return {
        heading: `How teams use ${feature?.title || 'this feature'}`,
        intro: feature?.description || 'Discover the benefits for your daily workflow.',
        tabs: (feature?.benefits || []).slice(0, 3).map((benefit, index) => ({
            value: `benefit-${index + 1}`,
            label: `Workflow ${index + 1}`,
            title: benefit,
            description: feature?.description || '',
            items: feature?.benefits || [],
            badge: feature?.metric || "Built for daily operations",
        })),
    };
}
